import { match } from 'ts-pattern';
import type {
  ImageAttrAction,
  ImageAttrState,
  LinkAttrAction,
  LinkAttrState,
  ResolveImgData,
} from './types';

import {
  ImageAttrBatch,
  ImageAttrs,
  LinkAttrBatch,
  LinkAttrs,
  authUrl,
  hostNames,
  imageAttrStateDefault,
  integrationsClientId,
  integrationsRedirectPath,
  linkAttrStateDefault,
} from './constants';

export const isMovableInk = (url?: string) => {
  if (!url) return false;
  const urlObj = new URL(url);
  return hostNames.includes(urlObj.hostname);
};

// https://support.movableink.com/hc/en-us/articles/360007488334-Movable-Ink-Content-API
export const getAuthUrl = (state: string) => {
  const queryParams = new URLSearchParams('');
  // response_type: Specifies the type of authentication request being made. Pass "code" for
  // authorization grant flows (more secure, provides refresh token) or "token" for implicit (less
  // secure, expires in 2 hrs).
  queryParams.set('response_type', 'code');
  // client_id: The client ID provided to you by MovableInk. This identifies that your application
  // is authorized to request resources on behalf of users.
  queryParams.set('client_id', integrationsClientId);
  // state: [Optional] A random string used to ensure that the client making the request and
  // receiving the response are the same.
  queryParams.set('state', state);
  // redirect_uri: The URI that the user should be redirected to after authentication.
  queryParams.set('redirect_uri', `${window.location.origin}${integrationsRedirectPath}`);

  return `${authUrl}?${queryParams.toString()}`;
};

// Authorization grant
export const getAuthCode = () => {
  const search = window.location.search;
  const searchParams = new URLSearchParams(search);
  return {
    code: searchParams.get('code') || '',
    state: searchParams.get('state') || '', // should match state provided to MI, if we included one
  };
};

// Implicit grant
export const getAuthToken = () => {
  const hashFragment = window.location.hash.substring(1); // hash without the hash mark
  const hashParams = new URLSearchParams(hashFragment);
  return {
    access_token: hashParams.get('access_token') || '',
    token_type: hashParams.get('token_type') || '',
    expires_in: parseInt(hashParams.get('expires_in') as string, 10) || 0,
    state: hashParams.get('state') || '', // should match state provided to MI, if we included one
  };
};

function updateState<T>(initial: T) {
  return (added: Partial<T>): T => ({
    ...initial,
    ...added,
  });
}

export const imageAttrReducer = (state: ImageAttrState, action: ImageAttrAction) => {
  const withState = updateState(state);
  const stateValue = typeof action.value === 'string' ? {} : action.value;
  const stateString = typeof action.value === 'string' ? action.value : undefined;
  const changeAttr = (attribute: ImageAttrs) => () => withState({ [attribute]: stateString });

  return match(action.type)
    .with(ImageAttrBatch.SET_NEW, () => updateState(imageAttrStateDefault)(stateValue))
    .with(ImageAttrBatch.UPDATE_BATCH, () => withState(stateValue))
    .with(ImageAttrs.ALT_DESC, changeAttr(ImageAttrs.ALT_DESC))
    .with(ImageAttrs.HEIGHT, changeAttr(ImageAttrs.HEIGHT))
    .with(ImageAttrs.SOURCE, changeAttr(ImageAttrs.SOURCE))
    .with(ImageAttrs.STYLES, changeAttr(ImageAttrs.STYLES))
    .with(ImageAttrs.WIDTH, changeAttr(ImageAttrs.WIDTH))
    .otherwise(() => state);
};

export const linkAttrReducer = (state: LinkAttrState, action: LinkAttrAction) => {
  const withState = updateState(state);
  const stateValue = typeof action.value === 'string' ? {} : action.value;
  const stateString = typeof action.value === 'string' ? action.value : undefined;
  const changeAttr = (attribute: LinkAttrs) => () => withState({ [attribute]: stateString });

  return match(action.type)
    .with(LinkAttrBatch.SET_NEW, () => updateState(linkAttrStateDefault)(stateValue))
    .with(LinkAttrBatch.UPDATE_BATCH, () => withState(stateValue))
    .with(LinkAttrs.HREF, changeAttr(LinkAttrs.HREF))
    .with(LinkAttrs.STYLES, changeAttr(LinkAttrs.STYLES))
    .otherwise(() => state);
};

export const getImageData = (src: string) =>
  new Promise((resolve: ResolveImgData) => {
    const image = new Image(); // used to load image into the canvas
    image.onload = () => {
      resolve(image);
    };
    image.onerror = () => {
      resolve({} as HTMLImageElement);
    };

    image.src = src;
  });

export const checkForSpecialCharacters = (str: string): boolean => {
  const specialChars = /[^\w\s-]/g;
  return specialChars.test(str);
};
